import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Save, Building2, DollarSign, MapPin, Calendar, ShieldCheck,
         CreditCard, Bell, FileText, Lock } from "lucide-react";
import { api } from "@/lib/api";

function Section({ title, icon: Icon, testid, children }) {
  return (
    <Card className="mb-4 bg-white/[0.03] border-white/10" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          {Icon && <Icon className="w-4 h-4 text-teal-300" />}
          <div className="text-sm font-display font-bold text-slate-100">{title}</div>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}
function Field({ label, testid, children, note }) {
  return (
    <label className="block mb-3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{label}</div>
      <div className="mt-1" data-testid={testid}>{children}</div>
      {note && <div className="text-[10px] text-slate-500 mt-0.5">{note}</div>}
    </label>
  );
}
function Toggle({ checked, onChange, testid, label }) {
  return (
    <label className="flex items-center gap-2 py-1" data-testid={testid}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-teal-500" />
      <span className="text-sm text-slate-200">{label}</span>
    </label>
  );
}
function Input(props) {
  return <input {...props} className={`w-full bg-slate-900 border border-white/10 rounded-md px-3 py-1.5 text-sm text-slate-100 ${props.className || ""}`} />;
}

export default function AdminSettings() {
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [zonesRaw, setZonesRaw] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setData(data);
      setZonesRaw((data.editable?.zones?.supported_postal_codes || []).join(", "));
      setDirty(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load settings"); }
  };
  useEffect(() => { load(); }, []);

  const setE = (path, val) => {
    setData((prev) => {
      const next = { ...prev, editable: { ...prev.editable } };
      const keys = path.split(".");
      let cur = next.editable;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...(cur[keys[i]] || {}) };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = val;
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const zones_arr = zonesRaw.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
      const editable = {
        ...data.editable,
        zones: { ...(data.editable.zones || {}), supported_postal_codes: zones_arr },
      };
      const { data: fresh } = await api.put("/admin/settings", { editable });
      setData(fresh);
      setZonesRaw((fresh.editable?.zones?.supported_postal_codes || []).join(", "));
      setDirty(false);
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="p-8 text-slate-400">Loading settings…</div>;
  const e = data.editable;
  const r = data.readonly;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 text-slate-100" data-testid="admin-settings-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-teal-300">Admin · Settings</div>
          <h1 className="font-display font-extrabold text-2xl mt-1">Platform settings</h1>
          <p className="text-sm text-slate-400 mt-1">Editable settings persist to the platform config. Sensitive & code-controlled values are shown as read-only.</p>
        </div>
        <button onClick={save} disabled={!dirty || saving} data-testid="settings-save" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-500 hover:bg-teal-400 text-slate-950 text-xs font-mono uppercase tracking-[0.16em] font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </div>

      <Section title="Business profile" icon={Building2} testid="settings-business">
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Business name"    testid="settings-business-name-wrap"><Input data-testid="settings-business-name"    value={e.business?.name || ""}          onChange={(ev) => setE("business.name", ev.target.value)} /></Field>
          <Field label="Support email"    testid="settings-business-email-wrap"><Input data-testid="settings-business-email"   value={e.business?.support_email || ""} onChange={(ev) => setE("business.support_email", ev.target.value)} /></Field>
          <Field label="Support phone"    testid="settings-business-phone-wrap"><Input data-testid="settings-business-phone"   value={e.business?.support_phone || ""} onChange={(ev) => setE("business.support_phone", ev.target.value)} /></Field>
          <Field label="Website"          testid="settings-business-website-wrap"><Input data-testid="settings-business-website" value={e.business?.website || ""}     onChange={(ev) => setE("business.website", ev.target.value)} /></Field>
          <Field label="Logo URL"         testid="settings-business-logo-wrap"><Input data-testid="settings-business-logo"    value={e.business?.logo_url || ""}     onChange={(ev) => setE("business.logo_url", ev.target.value)} /></Field>
          <Field label="Registered address" testid="settings-business-address-wrap"><Input data-testid="settings-business-address" value={e.business?.address || ""}   onChange={(ev) => setE("business.address", ev.target.value)} /></Field>
        </div>
      </Section>

      <Section title="Services & pricing" icon={DollarSign} testid="settings-services">
        <div className="text-xs text-slate-400 mb-3">Pricing tiers and add-ons are governed in code to preserve booking logic and ratings. This panel shows the live catalog and lets you toggle categories from the cleaner requirements list.</div>
        <div className="flex flex-wrap gap-2">
          {(r.service_categories || []).map((c) => (
            <span key={c} data-testid={`settings-cat-${c}`} className="inline-block px-2.5 py-1 rounded-full border border-teal-400/40 bg-teal-500/10 text-teal-200 font-mono text-[10px] uppercase tracking-[0.16em]">{c}</span>
          ))}
        </div>
        <div className="text-[11px] text-slate-500 mt-2">Categories: home · commercial · car · pet · outdoor · convenience. To change tier prices or add-ons, ship a code change so booking history stays consistent.</div>
      </Section>

      <Section title="Platform fees" icon={DollarSign} testid="settings-fees">
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="CLEANBAZ platform fee" testid="settings-fee-platform-wrap">
            <Input data-testid="settings-fee-platform" value={`${r.platform_fee_pct}%`} readOnly />
          </Field>
          <Field label="Cleaner payout" testid="settings-fee-cleaner-wrap">
            <Input data-testid="settings-fee-cleaner" value={`${r.cleaner_payout_pct}%`} readOnly />
          </Field>
        </div>
        <div className="text-[11px] text-amber-300 mt-2">Read-only — the 20/80 marketplace split is enforced in booking logic. Changing it here would silently mismatch historical payouts.</div>
      </Section>

      <Section title="Service areas / zones" icon={MapPin} testid="settings-zones">
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Supported countries" testid="settings-zone-countries-wrap">
            <Input data-testid="settings-zone-countries" value={(e.zones?.supported_countries || []).join(", ")}
              onChange={(ev) => setE("zones.supported_countries", ev.target.value.split(/[\s,]+/).filter(Boolean))} />
          </Field>
          <Field label="Supported postal codes (comma-separated)" note={`${(e.zones?.supported_postal_codes || []).length} zones · empty means every postal code is allowed`} testid="settings-zone-postal-wrap">
            <Input data-testid="settings-zone-postal" value={zonesRaw} onChange={(ev) => { setZonesRaw(ev.target.value); setDirty(true); }} placeholder="10001, 10002, 90210" />
          </Field>
        </div>
      </Section>

      <Section title="Booking settings" icon={Calendar} testid="settings-booking">
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Minimum lead time (hours)"     testid="settings-lead-wrap"><Input data-testid="settings-lead-hours"   type="number" value={e.booking?.min_lead_time_hours ?? 0}     onChange={(ev) => setE("booking.min_lead_time_hours", Number(ev.target.value))} /></Field>
          <Field label="Cancellation grace (hours)"    testid="settings-grace-wrap"><Input data-testid="settings-grace-hours" type="number" value={e.booking?.cancellation_grace_hours ?? 0} onChange={(ev) => setE("booking.cancellation_grace_hours", Number(ev.target.value))} /></Field>
          <Field label="Late-cancel fee %"             testid="settings-late-wrap"><Input data-testid="settings-late-pct"    type="number" value={e.booking?.late_cancel_fee_pct ?? 0}      onChange={(ev) => setE("booking.late_cancel_fee_pct", Number(ev.target.value))} /></Field>
          <Field label="Default currency"              testid="settings-currency-wrap"><Input data-testid="settings-currency"   value={e.booking?.default_currency || "USD"}      onChange={(ev) => setE("booking.default_currency", ev.target.value.toUpperCase())} /></Field>
        </div>
      </Section>

      <Section title="Cleaner requirements" icon={ShieldCheck} testid="settings-cleaner">
        <Toggle testid="settings-req-id"        checked={e.cleaner_requirements?.require_id_verification}          onChange={(v) => setE("cleaner_requirements.require_id_verification", v)}          label="Require ID verification before dispatch" />
        <Toggle testid="settings-req-agreement" checked={e.cleaner_requirements?.require_agreement_acceptance}     onChange={(v) => setE("cleaner_requirements.require_agreement_acceptance", v)}     label="Require Cleaner Agreement acceptance" />
        <Toggle testid="settings-req-insurance" checked={e.cleaner_requirements?.require_insurance_for_own_vehicle} onChange={(v) => setE("cleaner_requirements.require_insurance_for_own_vehicle", v)} label="Require auto insurance for cleaners using own vehicle" />
        <Toggle testid="settings-req-dl"        checked={e.cleaner_requirements?.require_drivers_license_for_own_vehicle} onChange={(v) => setE("cleaner_requirements.require_drivers_license_for_own_vehicle", v)} label="Require driver's license for cleaners using own vehicle" />
      </Section>

      <Section title="Payment settings" icon={CreditCard} testid="settings-payments">
        <div className="grid md:grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg border border-white/10 p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Stripe mode</div>
            <div className="mt-1 font-mono" data-testid="settings-stripe-mode">{r.stripe.mode}</div>
          </div>
          <div className="rounded-lg border border-white/10 p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Publishable key</div>
            <div className="mt-1 font-mono" data-testid="settings-stripe-pk">{r.stripe.publishable_set ? "configured ✓" : "missing"}</div>
          </div>
          <div className="rounded-lg border border-white/10 p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Webhook secret</div>
            <div className="mt-1 font-mono" data-testid="settings-stripe-webhook">{r.stripe.webhook_secret_set ? "configured ✓" : "missing"}</div>
          </div>
          <div className="rounded-lg border border-white/10 p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Stripe Connect</div>
            <div className="mt-1 font-mono" data-testid="settings-stripe-connect">{r.stripe.connect_enabled ? `enabled · ${r.stripe.connect_accounts_on_file} accounts on file` : "disabled"}</div>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 mt-2">Secret keys never leave the server. Rotate via Emergent Deployment → Secrets.</div>
      </Section>

      <Section title="Notifications" icon={Bell} testid="settings-notifications">
        <Toggle testid="settings-notif-booking"      checked={e.notifications?.email_new_booking}      onChange={(v) => setE("notifications.email_new_booking", v)}      label="Email admin on new booking" />
        <Toggle testid="settings-notif-complaint"   checked={e.notifications?.email_new_complaint}   onChange={(v) => setE("notifications.email_new_complaint", v)}   label="Email admin on new complaint / dispute" />
        <Toggle testid="settings-notif-verification" checked={e.notifications?.email_new_verification} onChange={(v) => setE("notifications.email_new_verification", v)} label="Email admin when a cleaner submits verification" />
        <Toggle testid="settings-notif-payouts"     checked={e.notifications?.email_payout_failures}  onChange={(v) => setE("notifications.email_payout_failures", v)}  label="Email admin on payout failures" />
      </Section>

      <Section title="Policies & agreements" icon={FileText} testid="settings-policies">
        <div className="grid md:grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg border border-white/10 p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Cleaner Agreement</div>
            <div className="mt-1 font-mono" data-testid="settings-policy-agreement">
              {r.policies.cleaner_agreement_latest?.version || "—"} · {r.policies.cleaner_agreement_versions} version(s)
            </div>
          </div>
          <div className="rounded-lg border border-white/10 p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Dispute policy</div>
            <div className="mt-1 font-mono" data-testid="settings-policy-dispute">
              {r.policies.dispute_policy_latest?.version || "—"} · {r.policies.dispute_policy_versions} version(s)
            </div>
          </div>
        </div>
      </Section>

      <Section title="Security / admin" icon={Lock} testid="settings-security">
        <div className="text-sm text-slate-300">
          Admin authentication and audit logging are managed by CLEANBAZ core. Every sensitive admin action is written to the <a className="text-teal-300 hover:underline" href="/admin/audit">audit log</a>.
        </div>
        <div className="mt-2 text-[11px] text-slate-500">Password rotation for the admin account: use the Account Security page while logged in as the admin.</div>
      </Section>
    </div>
  );
}
